import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Plus, Edit2, Trash2, LogOut, X, CheckCircle, Loader, Calendar, MapPin, Tag } from "lucide-react";

const BE = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = ["Academic", "Cultural", "Sports", "Holiday", "Admission", "Workshop", "Other"];
const CAMPUSES = [
  "All Campuses",
  "Main Campus (Nagawara)",
  "Nikoo Homes Campus",
  "Banaswadi Campus",
  "Banashankari Campus",
  "Sarjapur Campus",
  "RT Nagar Campus",
];

const emptyForm = {
  title: "", date: "", end_date: "", time: "", description: "",
  category: "Academic", campus: "All Campuses", image_url: "",
  location: "", school_code: "tipsblr", active: true,
};

export default function AdminEvents() {
  const [token, setToken]       = useState(() => sessionStorage.getItem("admin_token") || "");
  const [password, setPassword] = useState("");
  const [authErr, setAuthErr]   = useState("");
  const [authLoad, setAuthLoad] = useState(false);

  const [events, setEvents]     = useState([]);
  const [loading, setLoading]   = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm]         = useState(emptyForm);
  const [editId, setEditId]     = useState(null);
  const [saving, setSaving]     = useState(false);
  const [toast, setToast]       = useState("");
  const [deleting, setDeleting] = useState(null);

  const authHeader = { Authorization: `Bearer ${token}` };

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BE}/api/events`);
      setEvents(res.data);
    } catch (e) {
      showToast("Failed to load events");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (token) fetchEvents(); }, [token, fetchEvents]);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(""), 3000);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthLoad(true); setAuthErr("");
    try {
      const res = await axios.post(`${BE}/api/admin/login`, { password });
      sessionStorage.setItem("admin_token", res.data.token);
      setToken(res.data.token);
    } catch {
      setAuthErr("Incorrect password.");
    } finally {
      setAuthLoad(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem("admin_token");
    setToken("");
  };

  const openAdd = () => { setForm(emptyForm); setEditId(null); setShowForm(true); };
  const openEdit = (ev) => {
    setForm({
      title: ev.title || "", date: ev.date || "", end_date: ev.end_date || "",
      time: ev.time || "", description: ev.description || "",
      category: ev.category || "Academic", campus: ev.campus || "All Campuses",
      image_url: ev.image_url || "", location: ev.location || "",
      school_code: ev.school_code || "tipsblr", active: ev.active !== false,
    });
    setEditId(ev.id); setShowForm(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editId) {
        await axios.put(`${BE}/api/events/${editId}`, form, { headers: authHeader });
        showToast("Event updated!");
      } else {
        await axios.post(`${BE}/api/events`, form, { headers: authHeader });
        showToast("Event created!");
      }
      setShowForm(false);
      fetchEvents();
    } catch {
      showToast("Save failed. Check session.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this event?")) return;
    setDeleting(id);
    try {
      await axios.delete(`${BE}/api/events/${id}`, { headers: authHeader });
      showToast("Event deleted.");
      fetchEvents();
    } catch {
      showToast("Delete failed.");
    } finally {
      setDeleting(null);
    }
  };

  // ── Login screen ─────────────────────────────────────────────────────────────
  if (!token) return (
    <div className="min-h-screen bg-[#1A0505] flex items-center justify-center px-4">
      <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-sm">
        <div className="text-center mb-6">
          <div className="w-14 h-14 bg-[#FEE7E9] rounded-2xl flex items-center justify-center mx-auto mb-3">
            <Calendar size={28} className="text-[#9B1522]" />
          </div>
          <h1 className="text-xl font-bold text-gray-900" style={{ fontFamily: "'Poppins', sans-serif" }}>Events Admin</h1>
          <p className="text-sm text-gray-500 mt-1">TIPS Bengaluru</p>
        </div>
        <form onSubmit={handleLogin} className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">Admin Password</label>
            <input
              type="password" required value={password} autoFocus
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter password"
              data-testid="admin-password-input"
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#B41C2C] focus:ring-1 focus:ring-[#B41C2C]"
            />
          </div>
          {authErr && <p className="text-xs text-red-600">{authErr}</p>}
          <button
            type="submit" disabled={authLoad}
            data-testid="admin-login-btn"
            className="w-full bg-[#9B1522] hover:bg-[#87111E] text-white font-bold py-2.5 rounded-xl transition-colors disabled:opacity-70 flex items-center justify-center gap-2"
          >
            {authLoad ? <><Loader size={15} className="animate-spin" /> Logging in…</> : "Sign In"}
          </button>
        </form>
      </div>
    </div>
  );

  // ── Main admin dashboard ──────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Toast */}
      {toast && (
        <div className="fixed top-4 right-4 z-50 bg-gray-900 text-white px-4 py-2.5 rounded-xl text-sm flex items-center gap-2 shadow-xl animate-fade-in">
          <CheckCircle size={15} className="text-green-400" /> {toast}
        </div>
      )}

      {/* Header */}
      <div className="bg-[#1A0505] text-white px-6 py-4 flex items-center justify-between shadow">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-[#9B1522] rounded-lg flex items-center justify-center">
            <Calendar size={18} />
          </div>
          <div>
            <h1 className="font-bold text-sm" style={{ fontFamily: "'Poppins', sans-serif" }}>TIPS Events Admin</h1>
            <p className="text-xs text-gray-400">Bengaluru</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={openAdd}
            data-testid="add-event-btn"
            className="flex items-center gap-2 bg-[#9B1522] hover:bg-[#87111E] text-white text-xs font-semibold px-4 py-2 rounded-lg transition-colors"
          >
            <Plus size={14} /> Add Event
          </button>
          <button onClick={handleLogout} className="text-gray-400 hover:text-white transition-colors">
            <LogOut size={18} />
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Stats bar */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-bold text-gray-900" style={{ fontFamily: "'Poppins', sans-serif" }}>
            All Events <span className="text-gray-400 font-normal text-base">({events.length})</span>
          </h2>
          <a href="/" className="text-xs text-[#B41C2C] hover:text-[#9B1522] font-semibold">← View Website</a>
        </div>

        {/* Events table */}
        {loading ? (
          <div className="text-center py-16 text-gray-400"><Loader size={28} className="animate-spin mx-auto" /></div>
        ) : events.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <Calendar size={48} className="mx-auto mb-3 opacity-30" />
            <p className="font-medium">No events yet</p>
            <button onClick={openAdd} className="mt-4 text-sm text-[#B41C2C] font-semibold hover:text-[#9B1522]">Add your first event</button>
          </div>
        ) : (
          <div className="space-y-3" data-testid="events-list">
            {events.map((ev) => (
              <div key={ev.id} className="bg-white rounded-xl border border-gray-100 p-4 flex items-start gap-4 hover:shadow-sm transition-shadow" data-testid="event-row">
                {/* Date block */}
                <div className="flex-shrink-0 w-14 text-center bg-[#FEE7E9] rounded-xl py-2">
                  <div className="text-xs text-[#B41C2C] font-bold uppercase">{new Date(ev.date + "T00:00:00").toLocaleDateString("en-IN", { month: "short" })}</div>
                  <div className="text-xl font-bold text-[#9B1522] leading-none">{new Date(ev.date + "T00:00:00").getDate()}</div>
                </div>
                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-bold text-gray-900 text-sm leading-snug">{ev.title}</h3>
                    <span className={`flex-shrink-0 text-xs px-2 py-0.5 rounded-full font-semibold ${ev.active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                      {ev.active ? "Active" : "Hidden"}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-3 mt-1.5">
                    <span className="flex items-center gap-1 text-xs text-gray-500"><Tag size={11} />{ev.category}</span>
                    <span className="flex items-center gap-1 text-xs text-gray-500"><MapPin size={11} />{ev.campus}</span>
                    {ev.time && <span className="flex items-center gap-1 text-xs text-gray-500"><Calendar size={11} />{ev.time}</span>}
                  </div>
                  {ev.description && <p className="text-xs text-gray-400 mt-1 line-clamp-1">{ev.description}</p>}
                </div>
                {/* Actions */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button onClick={() => openEdit(ev)} data-testid="edit-event-btn" className="w-8 h-8 bg-gray-100 hover:bg-[#FEE7E9] rounded-lg flex items-center justify-center transition-colors group">
                    <Edit2 size={14} className="text-gray-500 group-hover:text-[#9B1522]" />
                  </button>
                  <button onClick={() => handleDelete(ev.id)} disabled={deleting === ev.id} data-testid="delete-event-btn" className="w-8 h-8 bg-gray-100 hover:bg-red-50 rounded-lg flex items-center justify-center transition-colors group disabled:opacity-50">
                    {deleting === ev.id ? <Loader size={14} className="animate-spin text-gray-400" /> : <Trash2 size={14} className="text-gray-500 group-hover:text-red-600" />}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add / Edit Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="event-form-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between rounded-t-2xl">
              <h3 className="font-bold text-gray-900" style={{ fontFamily: "'Poppins', sans-serif" }}>
                {editId ? "Edit Event" : "Add Event"}
              </h3>
              <button onClick={() => setShowForm(false)} className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center hover:bg-gray-200 transition-colors">
                <X size={16} />
              </button>
            </div>
            <form onSubmit={handleSave} className="p-6 space-y-4" data-testid="event-form">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Event Title *</label>
                <input required type="text" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="e.g. Annual Sports Day" data-testid="event-title-input"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#B41C2C] focus:ring-1 focus:ring-[#B41C2C]" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Date *</label>
                  <input required type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
                    data-testid="event-date-input"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#B41C2C] focus:ring-1 focus:ring-[#B41C2C]" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Time</label>
                  <input type="text" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })}
                    placeholder="e.g. 9:00 AM"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#B41C2C] focus:ring-1 focus:ring-[#B41C2C]" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Category</label>
                  <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-[#B41C2C] focus:ring-1 focus:ring-[#B41C2C]">
                    {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Campus</label>
                  <select value={form.campus} onChange={(e) => setForm({ ...form, campus: e.target.value })}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-[#B41C2C] focus:ring-1 focus:ring-[#B41C2C]">
                    {CAMPUSES.map((c) => <option key={c}>{c}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Description</label>
                <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Brief description of the event…"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm resize-none focus:outline-none focus:border-[#B41C2C] focus:ring-1 focus:ring-[#B41C2C]" />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Image URL <span className="text-gray-400 font-normal">(optional)</span></label>
                <input type="url" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                  placeholder="https://… (paste an image link)"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#B41C2C] focus:ring-1 focus:ring-[#B41C2C]" />
                {form.image_url && (
                  <img src={form.image_url} alt="preview" className="mt-2 w-full h-28 object-cover rounded-lg border border-gray-200"
                    onError={(e) => { e.target.style.display = "none"; }} />
                )}
              </div>

              <div className="flex items-center gap-2 pt-1">
                <input type="checkbox" id="active" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })}
                  className="w-4 h-4 accent-[#9B1522]" />
                <label htmlFor="active" className="text-xs font-semibold text-gray-600">Show on website</label>
              </div>

              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowForm(false)}
                  className="flex-1 border border-gray-200 text-gray-700 font-semibold py-2.5 rounded-xl hover:bg-gray-50 transition-colors text-sm">
                  Cancel
                </button>
                <button type="submit" disabled={saving} data-testid="save-event-btn"
                  className="flex-1 bg-[#9B1522] hover:bg-[#87111E] text-white font-bold py-2.5 rounded-xl transition-colors disabled:opacity-70 flex items-center justify-center gap-2 text-sm">
                  {saving ? <><Loader size={14} className="animate-spin" /> Saving…</> : editId ? "Update Event" : "Create Event"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
